/**
 * Axios instance for sk-notification-api (:3005). That service verifies the
 * platform JWT from Authorization: Bearer ONLY (requirePlatformJwt) — it does
 * NOT read the httpOnly cookie — so we attach the platform access token as a
 * Bearer here. On 401 we refresh once via the platform (cookie-based) and
 * retry; a failed refresh hands off to the platform auth (redirectToPlatformAuth).
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { fetchAccessToken } from '../services/auth/auth';

const notificationClient = axios.create({
  withCredentials: true, // carries the platform refresh cookie for same-host refresh
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// In-memory access token (never persisted — it's short-lived). Seeded lazily
// on the first request and refreshed on 401.
let _accessToken: string | null = null;
let _tokenInFlight: Promise<string> | null = null;

function loadToken(): Promise<string> {
  if (!_tokenInFlight) {
    _tokenInFlight = fetchAccessToken()
      .then((t) => { _accessToken = t; return t; })
      .finally(() => { _tokenInFlight = null; });
  }
  return _tokenInFlight;
}

notificationClient.interceptors.request.use(async (config) => {
  if (!_accessToken) {
    try { await loadToken(); } catch { /* boot gate / 401 path handles no-session */ }
  }
  if (_accessToken) config.headers.Authorization = `Bearer ${_accessToken}`;
  return config;
});

notificationClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    // A 401 from :3005 means the Bearer was stale/invalid — refresh the platform
    // token ONCE and replay. We do NOT redirect on failure: the platform session
    // (cookie) is still valid, and boot already gates auth. Redirecting here would
    // ping-pong (data 401 → redirect → platform bounces back → data 401 …). Let
    // the screen surface the error instead — matches sk-catalog's axiosInstance.
    if (error.response?.status === 401 && original && !original._retried) {
      original._retried = true;
      _accessToken = null; // force a fresh token
      try {
        const token = await loadToken();
        original.headers.Authorization = `Bearer ${token}`;
        return await notificationClient(original);
      } catch {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  },
);

export default notificationClient;
